import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, RotateCcw } from 'lucide-react'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { croppedPixelDensity } from '../utilities/image-crop'
import { WidgetInputComplexNumericField, WidgetLabel } from './inputs'

type NudgeDir = 'up' | 'down' | 'left' | 'right'

function useImageNaturalSize(src: string | null) {
	const [size, setSize] = useState<{ w: number; h: number } | null>(null)
	const prevSrc = useRef<string | null>(null)

	useEffect(() => {
		if (!src) {
			setSize(null)
			return
		}
		if (src === prevSrc.current) { return }
		prevSrc.current = src

		const img = new Image()
		img.onload = () => setSize({ w: img.naturalWidth, h: img.naturalHeight })
		img.onerror = () => setSize(null)
		img.src = src
	}, [src])

	return size
}

function clamp(value: number, min: number, max: number) {
	return Math.min(Math.max(value, min), max)
}

function round(value: number, decimals: number) {
	const factor = 10 ** decimals
	return Math.round(value * factor) / factor
}

export type ImageCropGuideProps = {
	src: string
	imageScale: number
	imageOffsetX: number
	imageOffsetY: number
	/** Width of the layer element the background fills */
	containerWidth: number
	/** Height of the layer element the background fills */
	containerHeight: number
	/** Fixed pixel width, or `'fill'` to stretch to the container */
	width?: number | 'fill'
	/** Set to 100 where the frame must stay covered — below that the image letterboxes. */
	minScale?: number
	/** Ceiling for the scale field and the scroll wheel. */
	maxScale?: number
	/**
	 * CSS width the crop will actually be displayed at. Supplying it turns on a warning
	 * once the crop is enlarged past the source's own resolution.
	 */
	displayedWidthPx?: number
	/** Listen for arrow keys on the document and nudge the image, unless an input is focused */
	enableGlobalKeyboardNudge?: boolean
	onOffsetChange?: (x: number, y: number, transient?: boolean) => void
	onScaleChange?: (scale: number, transient?: boolean) => void
}

export function ImageCropGuide({
	src,
	imageScale,
	imageOffsetX,
	imageOffsetY,
	containerWidth,
	containerHeight,
	width: widthProp = 240,
	minScale = 10,
	maxScale,
	displayedWidthPx,
	enableGlobalKeyboardNudge = false,
	onOffsetChange,
	onScaleChange,
}: ImageCropGuideProps) {
	const { t } = useTranslation('common')
	const imgSize = useImageNaturalSize(src)
	const containerRef = useRef<HTMLDivElement>(null)
	// Held in state, not a ref: the preview only mounts once the image has a size, and
	// an effect keyed on a ref would have already run against null by then.
	const [previewEl, setPreviewEl] = useState<HTMLDivElement | null>(null)
	const [measuredWidth, setMeasuredWidth] = useState(0)

	// A ResizeObserver alone leaves the guide stuck at zero width when it mounts inside
	// a hidden tab panel, so take the width synchronously first and let the observer
	// handle changes after that.
	useLayoutEffect(() => {
		if (widthProp !== 'fill') { return }
		const el = containerRef.current
		if (!el) { return }
		setMeasuredWidth(el.clientWidth)
	})

	useEffect(() => {
		if (widthProp !== 'fill') { return }
		const el = containerRef.current
		if (!el) { return }
		const observer = new ResizeObserver(([entry]) => {
			setMeasuredWidth(entry.contentRect.width)
		})
		observer.observe(el)
		return () => observer.disconnect()
	}, [widthProp])

	const [flashDir, setFlashDir] = useState<NudgeDir | null>(null)
	const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	useEffect(() => () => {
		if (flashTimerRef.current) { clearTimeout(flashTimerRef.current) }
	}, [])

	// Returns true if a nudge actually produced a change (i.e. not clamped at an edge).
	function performNudge(dir: NudgeDir, transient = false): boolean {
		// Flash even when clamped at an edge so the user still sees the key registered
		setFlashDir(dir)
		if (flashTimerRef.current) { clearTimeout(flashTimerRef.current) }
		flashTimerRef.current = setTimeout(() => setFlashDir(null), 150)

		if (!onOffsetChange) { return false }
		const dx = dir === 'left' ? -1 : dir === 'right' ? 1 : 0
		const dy = dir === 'up' ? -1 : dir === 'down' ? 1 : 0
		const step = 100 / Math.max(imageScale, 100)
		const newOffsetX = round(clamp(imageOffsetX + dx * step, 0, 100), 2)
		const newOffsetY = round(clamp(imageOffsetY + dy * step, 0, 100), 2)

		if (newOffsetX === imageOffsetX && newOffsetY === imageOffsetY) { return false }
		onOffsetChange(newOffsetX, newOffsetY, transient)
		return true
	}

	// Latest nudge function for the document-level keyboard listener and the hold interval —
	// read via ref so listeners don't re-attach on every offset/scale change.
	const performNudgeRef = useRef(performNudge)
	performNudgeRef.current = performNudge

	// Final commit for a session of transient updates — sends the current offset without the
	// transient flag so the canvas store finalizes it into undo history.
	const commitOffsetRef = useRef<() => void>(() => {})
	commitOffsetRef.current = () => {
		onOffsetChange?.(imageOffsetX, imageOffsetY)
	}

	// Press-and-hold on the arrow buttons: immediate nudge, then a delay, then a repeating
	// interval. All nudges during the hold are transient; release commits the final state.
	const holdRef = useRef<{ stop: () => void } | null>(null)

	function startHold(dir: NudgeDir) {
		holdRef.current?.stop()
		let dirty = performNudge(dir, true)
		let intervalId: ReturnType<typeof setInterval> | null = null
		const delayId = setTimeout(() => {
			intervalId = setInterval(() => {
				if (performNudgeRef.current(dir, true)) { dirty = true }
			}, 80)
		}, 350)
		holdRef.current = {
			stop: () => {
				clearTimeout(delayId)
				if (intervalId) { clearInterval(intervalId) }
				if (dirty) { commitOffsetRef.current() }
				holdRef.current = null
			},
		}
	}

	function stopHold() {
		holdRef.current?.stop()
	}

	useEffect(() => () => holdRef.current?.stop(), [])

	useEffect(() => {
		if (!enableGlobalKeyboardNudge) { return }
		const heldArrows = new Set<string>()
		let dirty = false

		function handleKeyDown(e: KeyboardEvent) {
			const active = document.activeElement
			if (active instanceof HTMLInputElement) { return }
			if (active instanceof HTMLTextAreaElement) { return }
			if (active instanceof HTMLElement && active.isContentEditable) { return }

			let dir: NudgeDir | null = null
			if (e.key === 'ArrowUp') { dir = 'up' }
			else if (e.key === 'ArrowDown') { dir = 'down' }
			else if (e.key === 'ArrowLeft') { dir = 'left' }
			else if (e.key === 'ArrowRight') { dir = 'right' }
			if (!dir) { return }

			e.preventDefault()
			heldArrows.add(e.key)
			if (performNudgeRef.current(dir, true)) { dirty = true }
		}

		function handleKeyUp(e: KeyboardEvent) {
			if (!heldArrows.delete(e.key)) { return }
			if (heldArrows.size === 0 && dirty) {
				commitOffsetRef.current()
				dirty = false
			}
		}

		document.addEventListener('keydown', handleKeyDown)
		document.addEventListener('keyup', handleKeyUp)
		return () => {
			document.removeEventListener('keydown', handleKeyDown)
			document.removeEventListener('keyup', handleKeyUp)
			// Popover closed (or feature flag flipped) while keys were held — finalize.
			if (dirty) { commitOffsetRef.current() }
		}
	}, [enableGlobalKeyboardNudge])

	// Read via ref so the native listener below never needs re-attaching.
	const applyWheelRef = useRef<(deltaY: number) => void>(() => {})
	applyWheelRef.current = (deltaY: number) => {
		if (!onScaleChange) { return }
		const raw = Math.max(minScale, round(imageScale + -deltaY * 0.5, 0))
		onScaleChange(maxScale === undefined ? raw : Math.min(raw, maxScale))
	}

	// React registers onWheel passively at the root, so preventDefault there is ignored
	// and the panel scrolls away underneath the zoom. A non-passive native listener is
	// the only way to keep the gesture on the preview.
	useEffect(() => {
		if (!previewEl || !onScaleChange) { return }

		function handleWheel(event: WheelEvent) {
			event.preventDefault()
			event.stopPropagation()
			applyWheelRef.current(event.deltaY)
		}

		previewEl.addEventListener('wheel', handleWheel, { passive: false })
		return () => previewEl.removeEventListener('wheel', handleWheel)
	}, [previewEl, onScaleChange])

	const guideWidth = widthProp === 'fill' ? measuredWidth : widthProp

	const dragRef = useRef<
		{
			startClientX: number
			startClientY: number
			startOffsetX: number
			startOffsetY: number
			lastOffsetX: number
			lastOffsetY: number
		} | null
	>(null)

	// Keep current offsets in a ref so X/Y input callbacks don't cross-invalidate
	const offsetRef = useRef({ imageOffsetX, imageOffsetY })
	offsetRef.current = { imageOffsetX, imageOffsetY }

	// Stable callbacks for the numeric inputs
	const handleScaleInputChange = useCallback((scale: number) => {
		onScaleChange?.(scale)
	}, [onScaleChange])

	const handleScaleInputTransient = useCallback((scale: number) => {
		onScaleChange?.(scale, true)
	}, [onScaleChange])

	const handleOffsetXChange = useCallback((x: number) => {
		onOffsetChange?.(x, offsetRef.current.imageOffsetY)
	}, [onOffsetChange])

	const handleOffsetXTransient = useCallback((x: number) => {
		onOffsetChange?.(x, offsetRef.current.imageOffsetY, true)
	}, [onOffsetChange])

	const handleOffsetYChange = useCallback((y: number) => {
		onOffsetChange?.(offsetRef.current.imageOffsetX, y)
	}, [onOffsetChange])

	const handleOffsetYTransient = useCallback((y: number) => {
		onOffsetChange?.(offsetRef.current.imageOffsetX, y, true)
	}, [onOffsetChange])

	const isFill = widthProp === 'fill'

	if (!imgSize || containerWidth === 0 || containerHeight === 0 || (isFill && measuredWidth === 0)) {
		return (
			<div
				ref={containerRef}
				className="flex items-center justify-center text-xs text-grey-400"
				style={{ width: isFill ? '100%' : guideWidth, height: guideWidth * 0.6 || 144 }}
			>
				{t('editor.crop.loading')}
			</div>
		)
	}

	// Replicate the cover+pan math from BackgroundFill
	const nativeScale = Math.max(containerWidth / imgSize.w, containerHeight / imgSize.h)
	const userScale = imageScale / 100
	const scaledW = imgSize.w * nativeScale * userScale
	const scaledH = imgSize.h * nativeScale * userScale

	const offsetX = clamp(imageOffsetX, 0, 100) / 100
	const offsetY = clamp(imageOffsetY, 0, 100) / 100

	// The preview encompasses the bounding box of (image, frame) per-axis so the
	// visualization works at any scale. Whichever rect is smaller in a given axis
	// is the one that slides with the offset — at >100% the frame moves within
	// the image (cropping), at <100% the image moves within the frame (positioning).
	const previewWorldW = Math.max(containerWidth, scaledW)
	const previewWorldH = Math.max(containerHeight, scaledH)

	const imgRectW = scaledW / previewWorldW
	const imgRectH = scaledH / previewWorldH
	const frameRectW = containerWidth / previewWorldW
	const frameRectH = containerHeight / previewWorldH

	const imgSlackX = 1 - imgRectW
	const imgSlackY = 1 - imgRectH
	const frameSlackX = 1 - frameRectW
	const frameSlackY = 1 - frameRectH

	const imgRectX = offsetX * imgSlackX
	const imgRectY = offsetY * imgSlackY
	const frameRectX = offsetX * frameSlackX
	const frameRectY = offsetY * frameSlackY

	// In each axis exactly one of the rects has slack (or both 0 when sizes match).
	const slackX = Math.max(imgSlackX, frameSlackX)
	const slackY = Math.max(imgSlackY, frameSlackY)

	// Fit the preview's bounding box into the guide
	const previewAspect = previewWorldW / previewWorldH
	const guideHeight = previewAspect >= 1
		? guideWidth / previewAspect
		: guideWidth
	const effectiveGuideWidth = previewAspect >= 1
		? guideWidth
		: guideWidth * previewAspect

	function handlePointerDown(e: React.PointerEvent) {
		if (!onOffsetChange) { return }
		e.preventDefault()
		e.currentTarget.setPointerCapture(e.pointerId)
		dragRef.current = {
			startClientX: e.clientX,
			startClientY: e.clientY,
			startOffsetX: imageOffsetX,
			startOffsetY: imageOffsetY,
			lastOffsetX: imageOffsetX,
			lastOffsetY: imageOffsetY,
		}
	}

	function handlePointerMove(e: React.PointerEvent) {
		const drag = dragRef.current
		if (!drag || !onOffsetChange) { return }

		const dx = e.clientX - drag.startClientX
		const dy = e.clientY - drag.startClientY

		const newOffsetX = slackX > 0
			? round(clamp(drag.startOffsetX + (dx / (slackX * effectiveGuideWidth)) * 100, 0, 100), 1)
			: drag.startOffsetX
		const newOffsetY = slackY > 0
			? round(clamp(drag.startOffsetY + (dy / (slackY * guideHeight)) * 100, 0, 100), 1)
			: drag.startOffsetY

		drag.lastOffsetX = newOffsetX
		drag.lastOffsetY = newOffsetY
		onOffsetChange(newOffsetX, newOffsetY, true)
	}

	function handlePointerUp() {
		const drag = dragRef.current
		if (!drag || !onOffsetChange) { return }
		onOffsetChange(drag.lastOffsetX, drag.lastOffsetY)
		dragRef.current = null
	}

	const pixelDensity = displayedWidthPx
		? croppedPixelDensity(
			{ imageScale, imageOffsetX, imageOffsetY },
			imgSize.w,
			imgSize.h,
			containerWidth / containerHeight,
			displayedWidthPx,
		)
		: null
	const isUpscaled = pixelDensity !== null && pixelDensity < 1

	const interactive = onOffsetChange || onScaleChange

	// Intersection of image and frame rects, in preview-normalized coords
	const intersectLeft = Math.max(imgRectX, frameRectX)
	const intersectTop = Math.max(imgRectY, frameRectY)
	const intersectRight = Math.min(imgRectX + imgRectW, frameRectX + frameRectW)
	const intersectBottom = Math.min(imgRectY + imgRectH, frameRectY + frameRectH)

	// Clip insets for the bright image, expressed in image-relative coords (0-100%)
	const clipLeft = imgRectW > 0 ? Math.max(0, (intersectLeft - imgRectX) / imgRectW) * 100 : 0
	const clipTop = imgRectH > 0 ? Math.max(0, (intersectTop - imgRectY) / imgRectH) * 100 : 0
	const clipRight = imgRectW > 0
		? Math.max(0, (imgRectX + imgRectW - intersectRight) / imgRectW) * 100
		: 0
	const clipBottom = imgRectH > 0
		? Math.max(0, (imgRectY + imgRectH - intersectBottom) / imgRectH) * 100
		: 0

	// Checkerboard for the empty frame area when the image doesn't fill the frame.
	// Pattern mirrors the one in StatefulImage so it reads consistently across the app.
	const showCheckerboard = imageScale < 100
	const checkerboardStyle: React.CSSProperties = showCheckerboard
		? {
			backgroundColor: 'white',
			backgroundImage:
				'linear-gradient(45deg, var(--color-grey-50) 25%, transparent 25%), linear-gradient(135deg, var(--color-grey-50) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, var(--color-grey-50) 75%), linear-gradient(135deg, transparent 75%, var(--color-grey-50) 75%)',
			backgroundSize: '16px 16px',
			backgroundPosition: '0 0, 8px 0, 8px -8px, 0 8px',
		}
		: {}

	return (
		<div ref={containerRef} style={{ width: isFill ? '100%' : effectiveGuideWidth }}>
			<div
				className={`relative overflow-hidden rounded ${showCheckerboard ? '' : 'bg-grey-100'}`}
				style={{
					width: effectiveGuideWidth,
					height: guideHeight,
					marginInline: isFill ? 'auto' : undefined,
					cursor: onOffsetChange ? 'grab' : undefined,
					...checkerboardStyle,
				}}
				ref={setPreviewEl}
				onPointerDown={onOffsetChange ? handlePointerDown : undefined}
				onPointerMove={onOffsetChange ? handlePointerMove : undefined}
				onPointerUp={onOffsetChange ? handlePointerUp : undefined}
				onPointerCancel={onOffsetChange ? handlePointerUp : undefined}
			>
				{/* Image at its rect (dimmed) */}
				<img
					src={src}
					alt=""
					draggable={false}
					className="absolute object-cover pointer-events-none"
					style={{
						left: `${imgRectX * 100}%`,
						top: `${imgRectY * 100}%`,
						width: `${imgRectW * 100}%`,
						height: `${imgRectH * 100}%`,
					}}
				/>
				{
					/* Dim overlay covers only the image area so the empty frame area
				    (checkerboard, at <100%) stays clearly visible. */
				}
				<div
					className="absolute bg-black/50 pointer-events-none"
					style={{
						left: `${imgRectX * 100}%`,
						top: `${imgRectY * 100}%`,
						width: `${imgRectW * 100}%`,
						height: `${imgRectH * 100}%`,
					}}
				/>

				{/* Bright copy clipped to the intersection of image and frame */}
				<img
					src={src}
					alt=""
					draggable={false}
					className="absolute object-cover pointer-events-none"
					style={{
						left: `${imgRectX * 100}%`,
						top: `${imgRectY * 100}%`,
						width: `${imgRectW * 100}%`,
						height: `${imgRectH * 100}%`,
						clipPath: `inset(${clipTop}% ${clipRight}% ${clipBottom}% ${clipLeft}%)`,
					}}
				/>

				{
					/* Frame border (visual indicator only — drag target is the whole preview).
				    Drawn entirely as inset shadows so the ring is never clipped when the
				    frame rect touches the preview edge (e.g. at offset 0 / 100). */
				}
				<div
					className="absolute pointer-events-none"
					style={{
						left: `${frameRectX * 100}%`,
						top: `${frameRectY * 100}%`,
						width: `${frameRectW * 100}%`,
						height: `${frameRectH * 100}%`,
						boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.3), inset 0 0 0 2px rgba(255,255,255,0.8)',
					}}
				/>
			</div>

			{interactive && (
				<div className="mt-2 flex flex-col gap-2">
					<WidgetLabel label={t('editor.crop.scale')}>
						<WidgetInputComplexNumericField
							value={imageScale}
							onChange={handleScaleInputChange}
							onTransientChange={handleScaleInputTransient}
							min={minScale}
							max={maxScale}
							defaultValue={100}
							suffix="%"
						/>
					</WidgetLabel>
					{isUpscaled && (
						<div
							role="alert"
							className="text-center text-[10px] text-yellow-800"
						>
							<p>{t('editor.crop.upscaleWarning')}</p>
						</div>
					)}
					{imageScale < 100 && (
						<div
							role="alert"
							className="text-center text-[10px] text-yellow-800"
						>
							<p>{t('editor.crop.letterboxWarning')}</p>
						</div>
					)}
					<WidgetLabel label={t('editor.crop.offset')}>
						<div className="grid grid-cols-2 gap-1">
							<WidgetInputComplexNumericField
								label="X"
								innerLabel="X"
								value={imageOffsetX}
								onChange={handleOffsetXChange}
								onTransientChange={handleOffsetXTransient}
								min={0}
								max={100}
								precision={0.01}
								defaultValue={50}
								suffix="%"
							/>
							<WidgetInputComplexNumericField
								label="Y"
								innerLabel="Y"
								value={imageOffsetY}
								onChange={handleOffsetYChange}
								onTransientChange={handleOffsetYTransient}
								min={0}
								max={100}
								precision={0.01}
								defaultValue={50}
								suffix="%"
							/>
						</div>
					</WidgetLabel>
					{onOffsetChange && (
						<div className="mt-1 grid grid-cols-3 items-center">
							<div />
							<div
								className="grid grid-cols-3 gap-0.5 justify-self-center"
								title={enableGlobalKeyboardNudge
									? t('editor.crop.nudgeWithArrowKeys')
									: t('editor.crop.nudge')}
							>
								<div />
								<NudgeArrowButton
									dir="up"
									active={flashDir === 'up'}
									onPress={() => startHold('up')}
									onRelease={stopHold}
								/>
								<div />
								<NudgeArrowButton
									dir="left"
									active={flashDir === 'left'}
									onPress={() => startHold('left')}
									onRelease={stopHold}
								/>
								<NudgeArrowButton
									dir="down"
									active={flashDir === 'down'}
									onPress={() => startHold('down')}
									onRelease={stopHold}
								/>
								<NudgeArrowButton
									dir="right"
									active={flashDir === 'right'}
									onPress={() => startHold('right')}
									onRelease={stopHold}
								/>
							</div>
							{onScaleChange && (
								<button
									type="button"
									onClick={() => {
										onScaleChange(100)
										onOffsetChange(50, 50)
									}}
									disabled={imageScale === 100 && imageOffsetX === 50 && imageOffsetY === 50}
									title={t('editor.crop.reset')}
									aria-label={t('editor.crop.reset')}
									className="flex size-5 items-center justify-center rounded border border-grey-200 text-grey-500 transition-colors justify-self-end hover:bg-grey-100 hover:text-grey-700 disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-grey-500"
								>
									<RotateCcw size={11} strokeWidth={2.5} />
								</button>
							)}
						</div>
					)}
				</div>
			)}
		</div>
	)
}

const NUDGE_ICONS = {
	up: ArrowUp,
	down: ArrowDown,
	left: ArrowLeft,
	right: ArrowRight,
}

function NudgeArrowButton(
	{ dir, active, onPress, onRelease }: {
		dir: NudgeDir
		active: boolean
		onPress: () => void
		onRelease: () => void
	},
) {
	const { t } = useTranslation('common')
	const Icon = NUDGE_ICONS[dir]
	return (
		<button
			type="button"
			tabIndex={-1}
			onPointerDown={(e) => {
				e.currentTarget.setPointerCapture(e.pointerId)
				onPress()
			}}
			onPointerUp={onRelease}
			onPointerCancel={onRelease}
			aria-label={t(`editor.crop.nudgeDirection.${dir}`)}
			className={`flex size-5 items-center justify-center rounded border transition-colors ${
				active
					? 'border-purple-300 bg-purple-100 text-purple-600'
					: 'border-grey-200 text-grey-500 hover:bg-grey-100 hover:text-grey-700'
			}`}
		>
			<Icon size={11} strokeWidth={2.5} />
		</button>
	)
}
